import type { Period, MonthStatus } from "./types";

const MONTH_NAMES = ["Янв","Фев","Мар","Апр","Май","Июн","Июл","Авг","Сен","Окт","Ноя","Дек"];
const MONTH_NAMES_FULL_RU = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];

export interface PeriodOption { id: string; label: string; }

const periodMonthsExpected: Record<Period, number> = { month: 1, quarter: 3, half: 6, year: 12 };

export function buildPeriodOptions(period: Period, availableMonths: string[]): PeriodOption[] {
  if (availableMonths.length === 0) return [];
  if (period === "month") {
    return availableMonths.map(id => {
      const parts = id.split("-");
      const y = Number(parts[0]);
      const m = Number(parts[1]);
      return { id, label: `${MONTH_NAMES_FULL_RU[m - 1] ?? ""} ${y}` };
    });
  }
  const expected = periodMonthsExpected[period];
  const map = new Map<string, string[]>();
  for (const id of availableMonths) {
    const parts = id.split("-");
    const y = Number(parts[0]);
    const mNum = Number(parts[1]);
    let groupId: string;
    if (period === "quarter") groupId = `Q${Math.ceil(mNum/3)}-${y}`;
    else if (period === "half") groupId = `H${mNum <= 6 ? 1 : 2}-${y}`;
    else groupId = `Y-${y}`;
    if (!map.has(groupId)) map.set(groupId, []);
    map.get(groupId)!.push(MONTH_NAMES[mNum - 1] ?? "");
  }
  return [...map.entries()].map(([id, shorts]) => {
    const isFull = shorts.length === expected;
    const first = shorts[0] ?? "";
    const last = shorts[shorts.length - 1] ?? "";
    const range = first === last ? first : `${first}–${last}`;
    let label: string;
    if (period === "quarter") {
      const [, q, y] = id.match(/^Q(\d)-(\d+)$/)!;
      label = `${q} квартал ${y} · ${isFull ? range : "доступно " + range}`;
    } else if (period === "half") {
      const [, h, y] = id.match(/^H(\d)-(\d+)$/)!;
      label = `${h} полугодие ${y} · ${isFull ? range : "доступно " + range}`;
    } else {
      const [, y] = id.match(/^Y-(\d+)$/)!;
      label = `${y} год · ${isFull ? range : "доступно " + range}`;
    }
    return { id, label };
  });
}

/** Месяц "YYYY-MM" входит в период periodId ("2026-10" | "Q4-2026" | "H2-2026" | "Y-2026")? */
export function monthInPeriod(month: string, period: Period, periodId: string): boolean {
  return buildPeriodOptions(period, [month])[0]?.id === periodId;
}

export interface PreliminaryInfo { months: string[]; updatedAt: number | null }

/** Предварительные (незакрытые) месяцы внутри выбранного периода и время последней публикации. */
export function getPreliminaryInfo(period: Period, periodId: string, statuses: MonthStatus[]): PreliminaryInfo | null {
  const prelim = statuses.filter(s => s.status === "preliminary" && monthInPeriod(s.month, period, periodId));
  if (prelim.length === 0) return null;
  const ts = prelim.map(s => s.published_at ?? 0).filter(x => x > 0);
  return { months: prelim.map(s => s.month).sort(), updatedAt: ts.length ? Math.max(...ts) : null };
}
